import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Gift, X, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const DISMISS_KEY = 'tokengate_referral_banner_dismissed_v1';

/**
 * Subtle "Refer & Earn" banner. Only shown to logged-in users. Dismissible;
 * the choice persists in localStorage so it never comes back for that user
 * on that device. Slides in below the hero on the homepage.
 */
export const ReferralBanner = () => {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(true); // start hidden to avoid flash

  useEffect(() => {
    if (!user) { setDismissed(true); return; }
    const isDismissed = localStorage.getItem(DISMISS_KEY) === '1';
    setDismissed(isDismissed);
  }, [user]);

  if (!user || dismissed) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6" data-testid="referral-banner">
      <div className="relative rounded-2xl border border-violet-200 dark:border-violet-500/25 bg-gradient-to-r from-violet-50 via-white to-fuchsia-50 dark:from-violet-500/10 dark:via-neutral-900/40 dark:to-fuchsia-500/10 backdrop-blur px-4 sm:px-6 py-4 flex items-center gap-4 overflow-hidden">
        {/* soft glow */}
        <div className="pointer-events-none absolute -right-16 -top-12 w-56 h-56 rounded-full blur-3xl opacity-30 bg-fuchsia-400 dark:opacity-20" />
        <div className="pointer-events-none absolute -left-12 -bottom-16 w-52 h-52 rounded-full blur-3xl opacity-20 bg-violet-400 dark:opacity-15" />

        <div className="relative shrink-0 h-11 w-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-violet-500 to-fuchsia-500 shadow-lg shadow-violet-500/20">
          <Gift className="w-5 h-5 text-white" />
        </div>

        <div className="relative min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-display font-bold text-base sm:text-lg text-neutral-900 dark:text-white">Refer &amp; Earn</span>
            <span className="text-[10px] uppercase tracking-widest font-bold text-[#8247E5] dark:text-[#C084FC]">10% + 10%</span>
          </div>
          <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-300 mt-0.5 leading-snug">
            Share your link. When a friend completes their first order, you both get 10% off your service fee. Codes land automatically in your account.
          </p>
        </div>

        <Link
          to="/dashboard/referrals"
          data-testid="referral-banner-cta"
          className="relative hidden sm:inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 text-xs font-semibold hover:opacity-90 transition-opacity shrink-0"
        >
          Get my link <ArrowRight className="w-3.5 h-3.5" />
        </Link>

        <button
          data-testid="referral-banner-dismiss"
          onClick={() => { localStorage.setItem(DISMISS_KEY, '1'); setDismissed(true); }}
          className="relative shrink-0 h-8 w-8 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 flex items-center justify-center text-neutral-400 hover:text-neutral-600 dark:hover:text-white"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ReferralBanner;
